import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * Translations live inline rather than in JSON files.
 *
 * Two locales and a few hundred keys: a bundler round trip and a loading state
 * would cost more than they buy, and keeping the strings in TypeScript means a
 * missing key in one language is a type error rather than a `[object Object]`
 * in production.
 */

const zh = {
  common: {
    appName: 'Cambio',
    appNameFull: 'Cambio · 汇记',
    cancel: '取消',
    save: '保存',
    delete: '删除',
    edit: '编辑',
    undo: '撤销',
    close: '关闭',
    confirm: '确定',
    loading: '加载中',
    retry: '重试',
    search: '搜索',
    add: '添加',
    all: '全部',
    demo: '演示',
    readOnly: '只读',
    today: '今天',
  },
  nav: {
    ledger: '账单',
    stats: '统计',
    timing: '复盘',
    analysis: '分析',
    settings: '设置',
    profile: '我的',
    workspace: '交易工作台',
    productLabel: '个人外汇账本',
    ledgerHint: '记录、持仓与今日价格',
    statsHint: '成本、金额与市场走势',
    timingHint: '判断每次换汇是否值得',
    analysisHint: '持仓统计与择时复盘',
    settingsHint: '账号、外观与数据',
    personalAccount: '个人账户',
  },
  landing: {
    accountNav: '账号入口',
    eyebrow: '个人外汇持仓与成本分析',
    tagline: '你换了几十次汇，均价到底是多少？',
    subline:
      '把每一笔换汇放进同一本账里。Cambio 记住你的真实成本，也告诉你今天这个价，值不值得换。',
    viewDemo: '打开演示账本',
    signIn: '登录',
    signUp: '注册',
    featureCostTitle: '真实持仓成本',
    featureCostBody: '买入更新移动加权均价；卖出只计算盈亏，不会把持仓成本带偏。',
    featureDecisionTitle: '今日决策卡',
    featureDecisionBody: '用今天的汇率对照真实均价，直接看这一笔会把成本往上还是往下带。',
    featureTimingTitle: '择时复盘',
    featureTimingBody: '把你的均价和同期市场、定额定投放在一张图上，看看判断是否真的有用。',
    workflowKicker: '从记录到判断',
    workflowTitle: '一份账本，回答三个问题。',
    workflowBody: '不是另一个汇率换算器。这里记录的是你付过的价格，以及这些选择后来变成了什么。',
    previewLabel: 'AUD 对人民币持仓分析示例',
    previewHistory: '演示账本 · 20 笔记录',
    previewAverage: '你的持仓均价',
    previewBasis: '已含手续费的真实成本',
    previewMarket: '今日市场汇率',
    previewUpdated: '较持仓均价低 0.1306',
    previewChartLabel: '持仓均价与市场汇率走势对比',
    previewAverageShort: '你的均价',
    previewMarketShort: '市场汇率',
    previewDecision: '今天的价格低于你的历史成本',
    previewDecisionBody: '如果你正准备买入澳元，这一笔会把持仓均价往下拉。',
    footerLabel: '个人外汇持仓与成本分析',
    footerCurrencies: '支持 32 种币种',
    footerPortable: 'CSV / JSON 可导入导出',
  },
  auth: {
    signIn: '登录',
    signUp: '注册',
    identifier: '用户名或邮箱',
    username: '用户名',
    email: '邮箱',
    password: '密码',
    displayName: '显示名称',
    baseCurrency: '记账本位币',
    noAccount: '还没有账号？',
    hasAccount: '已经有账号了？',
    signOut: '退出登录',
    demoHint: '想先看看？用演示账号进去逛一圈，不用注册。',
    tryDemo: '进入演示',
    changePassword: '修改密码',
    currentPassword: '当前密码',
    newPassword: '新密码',
    passwordChanged: '密码已修改，所有设备都需要重新登录',
  },
  ledger: {
    title: '账单',
    empty: '还没有记录',
    emptyHint: '记下第一笔换汇，均价和走势就都有了。',
    addFirst: '记第一笔',
    buy: '买入',
    sell: '卖出',
    rate: '汇率',
    amount: '金额',
    fee: '手续费',
    note: '备注',
    date: '日期',
    dateEstimated: '日期为推断',
    deleted: '已删除',
    noResults: '没有匹配的记录',
    searchPlaceholder: '搜索备注、金额、汇率',
    filterBuy: '买入',
    filterSell: '卖出',
    filterAmount: '金额',
    filterDate: '日期',
    newRecord: '新增记录',
    editRecord: '编辑记录',
    useMarketRate: '取现价',
    history: '交易流水',
    historyHint: '当前显示 {{count}} 笔，按发生时间排列',
    filterLabel: '筛选交易方向',
    tableDate: '日期',
    tableDirection: '方向',
    tableAmount: '外币金额',
    tableRate: '成交汇率',
    tableNote: '用途 / 备注',
    tableFee: '手续费',
    recordsInMonth: '{{count}} 笔',
    chooseCurrency: '选择币种',
    searchCurrency: '搜索币种或代码',
    chooseDate: '选择日期',
    previousMonth: '上个月',
    nextMonth: '下个月',
    selectedDate: '已选日期',
  },
  position: {
    holding: '持仓',
    averageRate: '均价',
    marketRate: '现价',
    realized: '已实现',
    unrealized: '浮动盈亏',
    cost: '投入成本',
    txCount: '笔数',
    flat: '当前无持仓',
    cheaperBy: '便宜 {{pct}}',
    dearerBy: '贵 {{pct}}',
    verdictBetter: '可以考虑',
    verdictWorse: '再等等',
    verdictNeutral: '和你的均价差不多',
    asOf: '数据截至 {{date}}',
  },
  simulator: {
    title: '换汇模拟',
    description: '试算一笔还没发生的兑换，看看均价会走到哪里。',
    ifIExchange: '如果现在',
    resultAverage: '均价',
    resultHolding: '持仓',
    noChange: '均价不变',
    record: '就这样记一笔',
  },
  stats: {
    title: '统计',
    costVsMarket: '成本 vs 市价',
    rateHistory: '成交汇率',
    amounts: '每笔金额',
    monthly: '按月汇总',
    yourAverage: '你的均价',
    executionRate: '成交价',
    marketRate: '市场价',
  },
  timing: {
    title: '择时复盘',
    score: '择时得分',
    vsMarket: '对比市场均价',
    vsDca: '对比定额定投',
    yourAverage: '你的均价',
    marketTwap: '同期市场均价',
    dcaBenchmark: '定投基准',
    ahead: '跑赢',
    behind: '跑输',
    inline: '基本持平',
    savedAmount: '省下 {{amount}}',
    lostAmount: '多花 {{amount}}',
    bestTrade: '最划算的一笔',
    worstTrade: '最亏的一笔',
    scenarios: '假设分析',
    scenarioLater: '全部推迟 {{days}} 天',
    scenarioEarlier: '全部提前 {{days}} 天',
    wouldCost: '成本变化',
    explainTwap:
      '市场均价按时间加权计算：每个报价按它实际维持的天数计权，所以周末不会被算漏，也不会被重复计算。',
    explainDca:
      '定投基准 = 如果每天固定花同样多的本位币换汇，你会得到的平均汇率。它是「什么都不想」的策略——跑赢它才算择时有价值。',
    noHistory: '还没有足够的历史汇率数据来做对比',
  },
  settings: {
    title: '设置',
    appearance: '外观',
    theme: '主题',
    themeLight: '浅色',
    themeDark: '深色',
    themeSystem: '跟随系统',
    language: '语言',
    currencies: '币种',
    data: '数据',
    exportCsv: '导出 CSV',
    exportJson: '导出 JSON',
    import: '导入',
    importHint: 'CSV 或 JSON。上面导出的文件可以原样导回，重复的记录会自动跳过。',
    importDone: '导入 {{inserted}} 条，跳过重复 {{skipped}} 条',
    importFailedRows: '{{count}} 行无法解析，其余已导入',
    importLine: '第 {{line}} 行',
    importNothing: '文件里没有可导入的记录',
    share: '分享链接',
    createShare: '创建只读链接',
    shareHint: '拿到链接的人可以看到持仓和统计，但看不到任何单条记录的备注。',
    revoke: '吊销',
    copied: '已复制',
    account: '账号',
  },
  errors: {
    generic: '出了点问题',
    network: '网络连接失败',
    readOnly: '演示账号是只读的，注册一个账号即可自由编辑',
    required: '这项是必填的',
    invalidNumber: '请输入一个数字',
    positive: '必须大于 0',
  },
};

const en: typeof zh = {
  common: {
    appName: 'Cambio',
    appNameFull: 'Cambio',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    undo: 'Undo',
    close: 'Close',
    confirm: 'Confirm',
    loading: 'Loading',
    retry: 'Retry',
    search: 'Search',
    add: 'Add',
    all: 'All',
    demo: 'Demo',
    readOnly: 'Read-only',
    today: 'Today',
  },
  nav: {
    ledger: 'Ledger',
    stats: 'Stats',
    timing: 'Review',
    analysis: 'Analysis',
    settings: 'Settings',
    profile: 'Profile',
    workspace: 'FX workspace',
    productLabel: 'Personal FX ledger',
    ledgerHint: "Records, positions and today's rate",
    statsHint: 'Cost, amounts and market movement',
    timingHint: 'Review whether each exchange paid off',
    analysisHint: 'Position stats and timing review',
    settingsHint: 'Account, appearance and data',
    personalAccount: 'Personal account',
  },
  landing: {
    accountNav: 'Account',
    eyebrow: 'Personal FX positions and cost analysis',
    tagline: 'Dozens of conversions later, what did you actually pay?',
    subline:
      "Keep every conversion in one ledger. Cambio remembers what you actually paid and puts today's rate in context.",
    viewDemo: 'Open demo ledger',
    signIn: 'Sign in',
    signUp: 'Sign up',
    featureCostTitle: 'Real cost basis',
    featureCostBody:
      'Buying updates the moving weighted average; selling records the result without distorting your cost basis.',
    featureDecisionTitle: "Today's call",
    featureDecisionBody:
      "Compare today's rate with your real average and see whether the next conversion moves your cost up or down.",
    featureTimingTitle: 'Timing review',
    featureTimingBody:
      'Put your average beside the market and a fixed DCA baseline to see whether your timing actually helped.',
    workflowKicker: 'From record to decision',
    workflowTitle: 'One ledger. Three useful answers.',
    workflowBody:
      'This is not another currency converter. It keeps the prices you paid and shows what those choices became.',
    previewLabel: 'Example AUD to CNY position analysis',
    previewHistory: 'Demo ledger · 20 entries',
    previewAverage: 'Your average cost',
    previewBasis: 'Real cost, including fees',
    previewMarket: "Today's market rate",
    previewUpdated: '0.1306 below your average',
    previewChartLabel: 'Average cost compared with the market rate',
    previewAverageShort: 'Your average',
    previewMarketShort: 'Market rate',
    previewDecision: 'Today is below your historical cost',
    previewDecisionBody:
      'If you are about to buy Australian dollars, this conversion would lower your average cost.',
    footerLabel: 'Personal FX position and cost analysis',
    footerCurrencies: '32 currencies',
    footerPortable: 'CSV / JSON import and export',
  },
  auth: {
    signIn: 'Sign in',
    signUp: 'Sign up',
    identifier: 'Username or email',
    username: 'Username',
    email: 'Email',
    password: 'Password',
    displayName: 'Display name',
    baseCurrency: 'Base currency',
    noAccount: 'No account yet?',
    hasAccount: 'Already have an account?',
    signOut: 'Sign out',
    demoHint: 'Want a look first? The demo account needs no sign-up.',
    tryDemo: 'Open demo',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    passwordChanged: 'Password changed. Every device has to sign in again.',
  },
  ledger: {
    title: 'Ledger',
    empty: 'Nothing recorded yet',
    emptyHint: 'Add your first conversion and the average and the chart follow.',
    addFirst: 'Add the first one',
    buy: 'Buy',
    sell: 'Sell',
    rate: 'Rate',
    amount: 'Amount',
    fee: 'Fee',
    note: 'Note',
    date: 'Date',
    dateEstimated: 'date inferred',
    deleted: 'Deleted',
    noResults: 'No matching records',
    searchPlaceholder: 'Search notes, amounts, rates',
    filterBuy: 'Buys',
    filterSell: 'Sells',
    filterAmount: 'Amount',
    filterDate: 'Date',
    newRecord: 'New record',
    editRecord: 'Edit record',
    useMarketRate: 'Use market rate',
    history: 'Transaction history',
    historyHint: '{{count}} records shown, newest first',
    filterLabel: 'Filter by direction',
    tableDate: 'Date',
    tableDirection: 'Direction',
    tableAmount: 'Foreign amount',
    tableRate: 'Execution rate',
    tableNote: 'Purpose / note',
    tableFee: 'Fee',
    recordsInMonth: '{{count}} records',
    chooseCurrency: 'Choose currency',
    searchCurrency: 'Search currencies or codes',
    chooseDate: 'Choose date',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    selectedDate: 'Selected date',
  },
  position: {
    holding: 'Holding',
    averageRate: 'Average',
    marketRate: 'Market',
    realized: 'Realised',
    unrealized: 'Unrealised',
    cost: 'Cost',
    txCount: 'Records',
    flat: 'No open position',
    cheaperBy: '{{pct}} cheaper',
    dearerBy: '{{pct}} dearer',
    verdictBetter: 'Worth considering',
    verdictWorse: 'Maybe wait',
    verdictNeutral: 'About the same as your average',
    asOf: 'As of {{date}}',
  },
  simulator: {
    title: 'What-if',
    description: 'Try a conversion that has not happened and see where the average lands.',
    ifIExchange: 'If I now',
    resultAverage: 'Average',
    resultHolding: 'Holding',
    noChange: 'Average unchanged',
    record: 'Record it',
  },
  stats: {
    title: 'Stats',
    costVsMarket: 'Cost vs market',
    rateHistory: 'Execution rates',
    amounts: 'Amount per trade',
    monthly: 'By month',
    yourAverage: 'Your average',
    executionRate: 'Executed',
    marketRate: 'Market',
  },
  timing: {
    title: 'Timing review',
    score: 'Timing score',
    vsMarket: 'vs market average',
    vsDca: 'vs fixed-amount plan',
    yourAverage: 'Your average',
    marketTwap: 'Market average',
    dcaBenchmark: 'DCA benchmark',
    ahead: 'ahead',
    behind: 'behind',
    inline: 'about even',
    savedAmount: 'saved {{amount}}',
    lostAmount: 'cost {{amount}} more',
    bestTrade: 'Best trade',
    worstTrade: 'Worst trade',
    scenarios: 'What if',
    scenarioLater: 'All {{days}} days later',
    scenarioEarlier: 'All {{days}} days earlier',
    wouldCost: 'Cost change',
    explainTwap:
      'Time-weighted: each quote counts for the number of days it actually stood, so a weekend is neither skipped nor double-counted.',
    explainDca:
      'The DCA benchmark is the average rate you would get by converting a fixed amount every day. It is the do-nothing strategy - beating it is what makes timing worth the effort.',
    noHistory: 'Not enough rate history yet to compare against',
  },
  settings: {
    title: 'Settings',
    appearance: 'Appearance',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    language: 'Language',
    currencies: 'Currencies',
    data: 'Data',
    exportCsv: 'Export CSV',
    exportJson: 'Export JSON',
    import: 'Import',
    importHint:
      'CSV or JSON. The files exported above go straight back in; duplicates are skipped.',
    importDone: 'Imported {{inserted}}, skipped {{skipped}} duplicates',
    importFailedRows: '{{count}} rows could not be parsed; the rest were imported',
    importLine: 'Line {{line}}',
    importNothing: 'Nothing in that file to import',
    share: 'Share link',
    createShare: 'Create read-only link',
    shareHint:
      'Anyone with the link sees positions and stats, but never the note on an individual record.',
    revoke: 'Revoke',
    copied: 'Copied',
    account: 'Account',
  },
  errors: {
    generic: 'Something went wrong',
    network: 'Network request failed',
    readOnly: 'The demo account is read-only. Sign up for one you can edit.',
    required: 'This field is required',
    invalidNumber: 'Enter a number',
    positive: 'Must be greater than 0',
  },
};

export const resources = {
  zh: { translation: zh },
  en: { translation: en },
} as const;

void i18next.use(initReactI18next).init({
  resources,
  lng: localStorage.getItem('cambio-locale') ?? 'zh',
  fallbackLng: 'zh',
  interpolation: { escapeValue: false },
});

export function setLocale(locale: 'zh' | 'en'): void {
  localStorage.setItem('cambio-locale', locale);
  void i18next.changeLanguage(locale);
  document.documentElement.lang = locale === 'zh' ? 'zh-CN' : 'en';
}

export default i18next;
